"use client";

import { Children, createContext, useContext, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { motion, useReducedMotion } from "motion/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { readSession, removeSession, writeSession } from "@/helpers/safeStorage";

// ---------------------------------------------------------------------------
// Ce que l'outil annonce à la page : son nombre d'étapes, son résultat
// ---------------------------------------------------------------------------

/**
 * Un petit magasin hors de React : l'en-tête d'un outil y annonce son nombre
 * d'étapes, et le résultat y dit qu'il est affiché. La page (la ligne sous le
 * titre, la carte « Et maintenant ? ») le lit sans que l'outil ait à la
 * connaître. Une page ne porte qu'un outil.
 */
function magasin(initial) {
    let valeur = initial;
    const abonnes = new Set();
    return {
        lire: () => valeur,
        serveur: () => initial,
        poser: (v) => {
            if (v === valeur) return;
            valeur = v;
            for (const a of abonnes) a();
        },
        abonner: (a) => {
            abonnes.add(a);
            return () => abonnes.delete(a);
        },
    };
}
const ETAPES = magasin(0);
const RESULTAT = magasin(false);

/** Le nombre d'étapes de l'outil de la page, 0 tant qu'il ne l'a pas dit. */
export function useEtapesAnnoncees() {
    return useSyncExternalStore(ETAPES.abonner, ETAPES.lire, ETAPES.serveur);
}

/** Vrai tant qu'un écran de résultat est affiché. */
export function useResultatAffiche() {
    return useSyncExternalStore(RESULTAT.abonner, RESULTAT.lire, RESULTAT.serveur);
}

/**
 * « 3 étapes », sous le titre de la fiche. Rendu vide par le serveur, qui ne
 * sait rien de l'outil : le nombre vient de sa barre de progression, donc il
 * est toujours juste, et absent pour un outil qui n'en a pas.
 */
export function EtapesAnnoncees({ modele, className = "", avant = null }) {
    const n = useEtapesAnnoncees();
    if (!n) return null;
    return (
        <span className={className}>
            {avant}
            {modele.replace("{n}", n)}
        </span>
    );
}

// ---------------------------------------------------------------------------
// Reprendre où l'on en était
// ---------------------------------------------------------------------------

/**
 * La reprise d'un parcours, fournie par ToolsDossier : la clé de l'outil, et
 * la demande de reprise quand le lecteur clique « Reprendre ».
 */
export const ContexteReprise = createContext(null);

export const PREFIXE_REPRISE = "believemy-reprise:";
/** Une reprise ne vaut que dans la journée : au-delà, on repart de zéro. */
export const DUREE_REPRISE = 24 * 3600 * 1000;

/**
 * Ce qu'on ne garde jamais, même le temps d'un onglet : le numéro de sécurité
 * sociale (décision du 26/09/2026, le NNI n'est jamais gardé), une signature
 * ou tout texte trop long pour être une réponse.
 */
function reponsesGardables(r) {
    if (!r || typeof r !== "object") return {};
    return Object.fromEntries(
        Object.entries(r).filter(
            ([cle, v]) => !/^(nni|nir)$|secu|signature(?!$)|dessin/i.test(cle) && !(typeof v === "string" && (v.length > 2000 || v.startsWith("data:"))),
        ),
    );
}

/** La reprise gardée pour un outil, ou null. */
export function lireReprise(cle) {
    try {
        const brut = JSON.parse(readSession(PREFIXE_REPRISE + cle) || "null");
        if (!brut || typeof brut.etape !== "string" || !Array.isArray(brut.pile) || Date.now() - brut.le > DUREE_REPRISE) return null;
        return brut;
    } catch (e) {
        return null;
    }
}

export function oublierReprise(cle) {
    removeSession(PREFIXE_REPRISE + cle);
}

/**
 * Les briques communes des parcours de /outils.
 *
 * Elles sortent du simulateur d'ACRE, où le format s'est réglé : une question
 * par écran, des réponses qui sont des situations et non des oui-non, de
 * grandes cartes à icône, un retour qui dépile, et une progression comptée en
 * phases nommées plutôt qu'en « étape 3 sur 7 » — un parcours qui se ramifie
 * ne connaît pas son propre total, et l'annoncer serait une promesse qu'on ne
 * tient pas. Le trait de la phase en cours avance en revanche à chaque écran
 * franchi : trois questions d'affilée sous « Vos clients » laissaient sinon la
 * barre immobile, et le lecteur avec l'impression de piétiner.
 *
 * DEUX ANIMATIONS, PAS TROIS. Chaque écran entre en fondu depuis le bas, et le
 * sens s'inverse quand on revient : c'est la seule chose qui dise au lecteur
 * s'il avance ou s'il recule. Les résultats chiffrés font monter leurs
 * montants. Rien d'autre ne bouge, et tout s'efface si le système demande à
 * réduire les animations.
 *
 * Les libellés restent chez l'appelant : chaque outil embarque son propre
 * dictionnaire français-anglais, parce que la mise en page publique ne reçoit
 * qu'une partie des messages next-intl côté client.
 */

/** Course d'entrée, en pixels. La maison ne dépasse jamais seize. */
export const COURSE = 12;
export const DUREE = 0.24;
export const ADOUCISSEMENT = [0.22, 1, 0.36, 1];
/** Montée des montants d'un résultat, en millisecondes. */
export const DUREE_COMPTEUR = 400;

// Le cadre des outils est la fiche de l'entreprise en haut de /outils : un
// blanc franc posé sur le dégradé de la page, un filet net et une ombre
// douce, au lieu d'un voile translucide qui se fondait dans le fond.
export const CADRE =
    "rounded-2xl border border-gray-300/80 dark:border-white/15 bg-white dark:bg-white/[0.05] shadow-[0_10px_30px_rgba(17,24,39,0.05)] p-5 sm:p-7 text-base leading-normal";

/**
 * Props d'entrée d'un écran.
 *
 * `sens` vaut 1 quand on avance, -1 quand on revient, 0 au tout premier rendu :
 * l'outil apparaît alors avec la page, sans avoir à s'annoncer, et aucun style
 * d'opacité nulle ne part dans le HTML rendu côté serveur.
 */
export function entree(sens, reduit) {
    if (reduit || sens === 0) return { initial: false };
    return {
        initial: { opacity: 0, y: sens * COURSE },
        animate: { opacity: 1, y: 0 },
        transition: { duration: DUREE, ease: ADOUCISSEMENT },
    };
}

/**
 * LA HAUTEUR SUIT, ELLE NE SAUTE PAS. D'un écran à l'autre, l'outil changeait
 * de hauteur d'un coup : tout ce qui est en dessous (l'encart de confiance,
 * la suite de la page) bondissait au moment où la nouvelle question
 * apparaissait. Le cadre suit maintenant la hauteur de son contenu en 280 ms.
 *
 * Le contenu n'est rogné que pendant le mouvement : au repos, les ombres, les
 * infobulles et les menus qui débordent restent visibles.
 */
export function HauteurFluide({ children }) {
    const reduit = useReducedMotion();
    const interieur = useRef(null);
    const [hauteur, setHauteur] = useState("auto");
    const [enMouvement, setEnMouvement] = useState(false);

    useEffect(() => {
        const el = interieur.current;
        if (!el || typeof ResizeObserver === "undefined") return undefined;
        const observateur = new ResizeObserver(([entree]) => {
            setHauteur(Math.round(entree.borderBoxSize?.[0]?.blockSize ?? entree.contentRect.height));
        });
        observateur.observe(el);
        return () => observateur.disconnect();
    }, []);

    if (reduit) return <div>{children}</div>;
    return (
        <motion.div
            initial={false}
            animate={{ height: hauteur }}
            transition={{ duration: 0.28, ease: ADOUCISSEMENT }}
            onAnimationStart={() => setEnMouvement(true)}
            onAnimationComplete={() => setEnMouvement(false)}
            style={{ overflow: enMouvement ? "hidden" : "visible" }}
        >
            <div ref={interieur}>{children}</div>
        </motion.div>
    );
}

/**
 * Une cascade : chaque bloc arrive 40 ms après le précédent, en fondu et
 * huit pixels de course. Rien au premier rendu ni quand le système demande
 * moins d'animations.
 */
function cascade(i, sens, reduit, depart = 0.08) {
    if (reduit || sens === 0) return { initial: false };
    return {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.28, ease: ADOUCISSEMENT, delay: depart + Math.min(i, 8) * 0.04 },
    };
}

// ---------------------------------------------------------------------------
// Les raccourcis clavier
// ---------------------------------------------------------------------------

/** Vrai quand un écran Ecran entoure le bouton : Entrée y fait avancer. */
const ContexteEntree = createContext(false);

/**
 * LE CLAVIER, COMME DANS UN BON FORMULAIRE : 1, 2, 3 choisissent une réponse,
 * Entrée fait avancer. Le raccourci ne part que si l'outil est à l'écran (pas
 * pendant qu'on lit la FAQ plus bas), hors d'une feuille ouverte, d'un champ
 * de texte long ou d'une liste déroulante, et jamais avec une touche de
 * modification (Cmd+1 change d'onglet).
 */
function useRaccourcis(ref, surTouche) {
    const gestionnaire = useRef(surTouche);
    useEffect(() => {
        gestionnaire.current = surTouche;
    });
    useEffect(() => {
        const ecouter = (e) => {
            if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;
            const cible = e.target instanceof Element ? e.target : null;
            if (cible?.closest('[role="dialog"], [contenteditable="true"], textarea, select')) return;
            const el = ref.current;
            if (!el) return;
            const cadre = el.getBoundingClientRect();
            if (cadre.bottom < 120 || cadre.top > window.innerHeight - 60) return;
            gestionnaire.current(e, cible);
        };
        window.addEventListener("keydown", ecouter);
        return () => window.removeEventListener("keydown", ecouter);
    }, [ref]);
}

/** Le petit repère de touche, seulement sur un écran piloté à la souris. */
const TOUCHE = "hidden [@media(hover:hover)_and_(pointer:fine)]:inline-flex items-center justify-center rounded-md font-semibold tabular-nums";

/**
 * Progression de 0 à 1, en easeOutCubic.
 *
 * Un compteur passe par un state et une boucle d'images, jamais par une
 * MotionValue rendue comme enfant : cette version de motion tente alors de
 * rendre l'objet lui-même et l'écran casse.
 */
export function useProgression(duree) {
    const [avancement, setAvancement] = useState(duree > 0 ? 0 : 1);

    useEffect(() => {
        if (duree <= 0) return undefined;
        let image;
        const depart = performance.now();
        const boucle = (t) => {
            const brut = Math.min(1, (t - depart) / duree);
            setAvancement(1 - Math.pow(1 - brut, 3));
            if (brut < 1) image = requestAnimationFrame(boucle);
        };
        image = requestAnimationFrame(boucle);
        return () => cancelAnimationFrame(image);
    }, [duree]);

    return avancement;
}

/**
 * La mécanique d'un parcours ramifié.
 *
 * Le retour dépile plutôt que de se déduire de l'écran courant : quand le
 * parcours se ramifie, l'écran précédent n'est pas une fonction de l'écran où
 * l'on se trouve.
 *
 * @param depart clé du premier écran.
 * @param reinitialiser remet à zéro les réponses de l'appelant.
 * @param phasesParEcran table écran → index de phase, celle que l'outil tient
 *        déjà pour nourrir l'en-tête. Elle sert ici à situer l'écran courant
 *        dans sa phase, et donc à faire avancer le trait.
 * @param reprise { etape, pile } quand le dossier du lecteur répond déjà aux
 *        premières questions (reprendre() dans lib/tools/dossier.js) : le
 *        parcours s'ouvre sur la première question qui reste, et le retour
 *        redescend celles qu'on a sautées, réponses déjà posées. « Recommencer »
 *        repart du vrai début.
 */
export function useParcours(depart, reinitialiser, phasesParEcran, reprise = null, garde = null) {
    const reduit = useReducedMotion();
    const [etape, setEtape] = useState(reprise?.etape ?? depart);
    const [pile, setPile] = useState(reprise?.pile ?? []);
    const [sens, setSens] = useState(0);

    // REPRENDRE OÙ L'ON EN ÉTAIT. Un outil qui passe ses réponses (`garde` :
    // { r, setR }) les voit gardées dans l'onglet à chaque pas, avec l'écran
    // et la pile. Un rechargement ou un aller-retour vers une autre page ne
    // fait plus tout perdre ; fermer l'onglet, si.
    const contexteReprise = useContext(ContexteReprise);
    const cleReprise = garde && contexteReprise?.cle ? contexteReprise.cle : null;
    useEffect(() => {
        // Au premier écran, rien à reprendre, et rien à effacer non plus : un
        // retour au départ n'est pas un « Recommencer », et le double montage
        // du mode développement passe par ici sans que le lecteur ait rien
        // fait. Seuls « Recommencer » et « Repartir de zéro » effacent.
        if (!cleReprise || (etape === depart && pile.length === 0)) return;
        writeSession(PREFIXE_REPRISE + cleReprise, JSON.stringify({ etape, pile, r: reponsesGardables(garde.r), le: Date.now() }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cleReprise, etape, pile, garde?.r]);

    // « Reprendre » : ToolsDossier pose la demande, le parcours s'y remet, une
    // fois par demande (ajusté pendant le rendu, sans effet en cascade).
    const demande = contexteReprise?.demande;
    const [demandeVue, setDemandeVue] = useState(null);
    if (demande && garde && demande.n !== demandeVue) {
        setDemandeVue(demande.n);
        garde.setR((x) => ({ ...x, ...demande.r }));
        setPile(demande.pile);
        setSens(1);
        setEtape(demande.etape);
    }

    const aller = (suivante) => {
        setSens(1);
        setPile((p) => [...p, etape]);
        setEtape(suivante);
    };

    const retour = () => {
        /* Rien à dépiler : le bouton ne ramènerait nulle part. Le cas se
           présente quand le retour du résultat est posé sur un parcours dont le
           résultat serait le premier écran. */
        if (pile.length === 0) return;
        setSens(-1);
        setPile((p) => {
            setEtape(p[p.length - 1]);
            return p.slice(0, -1);
        });
    };

    const recommencer = () => {
        setSens(-1);
        setEtape(depart);
        setPile([]);
        if (cleReprise) oublierReprise(cleReprise);
        if (reinitialiser) reinitialiser();
    };

    /* Rang de l'écran courant dans sa phase, en remontant la pile tant qu'on
       reste dans la même : c'est ce que le parcours sait de son avancée, et
       tout ce qu'il en sait. Le total, lui, dépend de la branche. */
    const rang = useMemo(() => {
        if (!phasesParEcran) return 1;
        const phase = phasesParEcran[etape];
        let n = 1;
        for (let i = pile.length - 1; i >= 0; i--) {
            if (phasesParEcran[pile[i]] !== phase) break;
            n++;
        }
        return n;
    }, [etape, pile, phasesParEcran]);

    return {
        etape,
        sens,
        reduit,
        rang,
        aller,
        retour,
        recommencer,
        /* La clé se pose à part sur chaque écran, jamais dans le spread, que
           React refuse : sans elle il réutilise le composant en place et
           l'entrée ne rejoue pas. */
        communs: { sens, reduit, rang },
    };
}

/**
 * Les traits de progression, un par phase.
 *
 * Le trait de la phase en cours se remplit selon le rang de l'écran dans cette
 * phase : la moitié au premier, deux tiers au deuxième, trois quarts au
 * troisième. La suite approche du bout sans jamais l'atteindre, parce qu'une
 * branche peut toujours poser une question de plus — annoncer le bout serait
 * la promesse d'un total que le parcours ne connaît pas. Il n'y a qu'au
 * résultat qu'il sait être arrivé, et les traits s'y remplissent tous.
 */
function Traits({ phases, phase, rang = 1, complet = false, reduit }) {
    const remplissage = (i) => {
        if (complet || i < phase) return "100%";
        if (i > phase) return "0%";
        return `${Math.round(100 * (1 - 1 / (rang + 1)))}%`;
    };

    return (
        <div className="flex gap-1.5">
            {phases.map((nom, i) => (
                <div
                    key={nom}
                    className="h-1.5 flex-1 rounded-full bg-gray-100 dark:bg-white/[0.06] overflow-hidden"
                >
                    <div
                        className={`h-full rounded-full bg-primary ${
                            reduit
                                ? ""
                                : "transition-[width] duration-300 ease-[cubic-bezier(.22,1,.36,1)]"
                        }`}
                        style={{ width: remplissage(i) }}
                    />
                </div>
            ))}
        </div>
    );
}

/**
 * Le bouton « ← Retour » des en-têtes et des résultats.
 *
 * Sa cible fait 44 pixels de haut, le minimum pour un doigt, sans que la
 * mise en page ne bouge : les marges négatives rendent la hauteur gagnée.
 */
function BoutonRetour({ onClick, libelle }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="-my-3 inline-flex min-h-[44px] items-center gap-1 text-sm text-gray-500 dark:text-lightBlue/70 hover:text-primary transition-colors duration-200"
        >
            <SolarIcon name="alt-arrow-left-linear" className="h-4 w-4" /> {libelle}
        </button>
    );
}

/** En-tête : retour à gauche, phase à droite, et les traits dessous. */
export function Entete({ phases, phase, rang, onRetour, retour, reduit }) {
    // Le nombre d'étapes, pour la ligne sous le titre de la fiche.
    const nombre = phases?.length || 0;
    useEffect(() => {
        if (nombre) ETAPES.poser(nombre);
    }, [nombre]);
    return (
        <div className="mb-6">
            <div className="flex justify-between items-baseline gap-3 mb-3">
                {onRetour ? (
                    <BoutonRetour onClick={onRetour} libelle={retour} />
                ) : (
                    <span />
                )}
                <span className="text-[11px] font-semibold text-[#D04515] dark:text-[#FF8A5B] uppercase tracking-wider shrink-0">
                    {phases[phase]}
                </span>
            </div>
            <Traits phases={phases} phase={phase} rang={rang} reduit={reduit} />
        </div>
    );
}

/** Une réponse : une grande carte, son icône, et la précision qui lève le doute. */
export function Choix({ icone, titre, detail, onClick, raccourci }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="group relative flex h-full w-full flex-col items-start justify-start text-left p-5 rounded-2xl border border-gray-200 dark:border-white/10 hover:border-[#D04515]/60 hover:bg-[#fff8f4] dark:hover:bg-white/[0.04] focus-visible:border-[#D04515]/60 focus-visible:bg-[#fff8f4] dark:focus-visible:bg-white/[0.04] transition-colors duration-200"
        >
            {/* L'icône dans une pastille, comme sur les lignes de /outils. Le
                contenu part du haut (flex-col) : un bouton centre le sien, et
                les icônes de cartes voisines ne tombaient pas à la même hauteur
                quand leurs textes n'avaient pas la même longueur. */}
            <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 dark:bg-white/[0.06] text-gray-500 dark:text-lightBlue/70 group-hover:bg-primary/10 group-hover:text-primary transition-colors duration-200">
                <SolarIcon name={icone} className="h-6 w-6" />
            </span>
            {raccourci && (
                <kbd aria-hidden="true" className={`${TOUCHE} absolute right-3.5 top-3.5 h-6 min-w-[1.5rem] border border-gray-200 dark:border-white/15 px-1.5 text-xs text-gray-400 dark:text-lightBlue/60 transition-colors duration-200 group-hover:border-[#D04515]/40 group-hover:text-[#B83C12] dark:group-hover:text-[#FF8A5B]`}>
                    {raccourci}
                </kbd>
            )}
            <div className="font-semibold dark:text-white leading-snug">{titre}</div>
            {detail && (
                <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1 leading-snug">
                    {detail}
                </div>
            )}
        </button>
    );
}

/**
 * Le titre d'un écran, en vrai titre de niveau 2.
 *
 * QUAND L'ÉCRAN CHANGE, LE FOCUS SUIT. La carte cliquée disparaît avec son
 * écran : sans rien faire, le focus retombait sur la page entière, et un
 * lecteur d'écran ou un utilisateur au clavier repartait du haut du site. Le
 * titre du nouvel écran reçoit donc le focus, sans faire défiler la page. Au
 * tout premier rendu (sens 0), rien ne bouge : l'outil s'affiche avec la page.
 */
function TitreEcran({ titre, sens, id }) {
    const ref = useFocusTitre(sens);
    return (
        <h2 ref={ref} id={id} tabIndex={-1} className="font-bold font-sourceSans text-xl sm:text-[22px] tracking-tight dark:text-white leading-snug outline-hidden mb-0">
            {titre}
        </h2>
    );
}

/**
 * Le focus qui suit l'écran, et le défilement qui le montre.
 *
 * Sur mobile, le nouvel écran peut être plus court que l'ancien : son titre se
 * retrouvait alors au-dessus du bord de l'écran, et le lecteur devant la
 * réponse d'une question qu'il ne voyait plus. Quand le titre est caché en
 * haut (sous l'en-tête fixe du site, 96 pixels), la page remonte jusqu'à lui,
 * en douceur sauf si le système demande de réduire les animations.
 */
function useFocusTitre(sens) {
    const ref = useRef(null);
    useEffect(() => {
        const titre = ref.current;
        if (sens === 0 || !titre) return;
        if (titre.getBoundingClientRect().top < 96) {
            const reduit = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
            window.scrollTo({
                top: window.scrollY + titre.getBoundingClientRect().top - 112,
                behavior: reduit ? "auto" : "smooth",
            });
        }
        titre.focus({ preventScroll: true });
        // Une seule fois par écran : chaque écran est remonté par sa clé.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return ref;
}

/**
 * L'identifiant du titre de l'écran en cours.
 *
 * Un champ de date posé seul sous la question n'a pas d'autre libellé que
 * cette question : ChampDate s'en sert pour se nommer.
 */
const ContexteEcran = createContext(null);

/** Un écran de question : l'en-tête, la question, et la grille de réponses. */
export function Question({
    phases,
    phase,
    rang,
    onRetour,
    retour,
    titre,
    aide,
    choix,
    sens,
    reduit,
}) {
    const cadre = useRef(null);
    useRaccourcis(cadre, (e, cible) => {
        if (cible?.closest("input")) return;
        const n = Number(e.key);
        if (Number.isInteger(n) && n >= 1 && n <= choix.length) {
            e.preventDefault();
            choix[n - 1].onClick();
        }
    });
    return (
        <motion.div ref={cadre} className={CADRE} {...entree(sens, reduit)}>
            <Entete
                phases={phases}
                phase={phase}
                rang={rang}
                onRetour={onRetour}
                retour={retour}
                reduit={reduit}
            />
            <TitreEcran titre={titre} sens={sens} />
            {aide && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-5">{aide}</p>
            )}
            <div
                className={`grid gap-3 ${choix.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
            >
                {/* Les réponses arrivent l'une après l'autre, juste après la
                    question : l'œil lit la question d'abord. */}
                {choix.map((c, i) => (
                    <motion.div key={c.titre} className="h-full" {...cascade(i, sens, reduit, 0.1)}>
                        <Choix {...c} raccourci={choix.length <= 9 ? i + 1 : undefined} />
                    </motion.div>
                ))}
            </div>
        </motion.div>
    );
}

/** Un écran de saisie : l'en-tête, la question, et ce que l'appelant y met. */
export function Ecran({
    phases,
    phase,
    rang,
    onRetour,
    retour,
    titre,
    aide,
    sens,
    reduit,
    children,
}) {
    const idTitre = useId();
    const cadre = useRef(null);
    useRaccourcis(cadre, (e, cible) => {
        if (e.key !== "Enter" || e.shiftKey) return;
        // Un bouton ou un lien réagit déjà à Entrée ; un formulaire se soumet seul.
        if (cible?.closest("button, a, form")) return;
        const boutons = [...(cadre.current?.querySelectorAll("[data-avancer]") || [])].filter((b) => !b.disabled);
        const bouton = boutons[boutons.length - 1];
        if (!bouton) return;
        e.preventDefault();
        bouton.click();
    });
    return (
        <motion.div ref={cadre} className={CADRE} {...entree(sens, reduit)}>
            <Entete
                phases={phases}
                phase={phase}
                rang={rang}
                onRetour={onRetour}
                retour={retour}
                reduit={reduit}
            />
            <TitreEcran titre={titre} sens={sens} id={idTitre} />
            {aide && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-5">{aide}</p>
            )}
            <ContexteEcran.Provider value={idTitre}>
                <ContexteEntree.Provider value>{children}</ContexteEntree.Provider>
            </ContexteEcran.Provider>
        </motion.div>
    );
}

/** Style commun des champs de saisie de la rubrique. */
export const STYLE_CHAMP =
    "w-full px-4 py-3 rounded-xl border bg-white dark:bg-white/[0.04] dark:text-white focus:outline-hidden focus:border-primary/60 focus-visible:ring-2 focus-visible:ring-[#D04515]/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-bgDark-800 disabled:opacity-40";

/**
 * Un champ de date, NON CONTRÔLÉ, et c'est tout l'intérêt.
 *
 * Un `<input type="date">` piloté par `value` est inutilisable au clavier :
 * tant que la date est incomplète, le navigateur rend une valeur vide, React
 * réécrit aussitôt l'ancienne, et les segments déjà tapés sautent. Le lecteur
 * voit « jj/mm/aaaa » revenir sous ses doigts, et croit que le champ refuse sa
 * saisie. Le défaut est resté invisible tant que les tests posaient la date
 * d'un coup, comme le fait le sélecteur du navigateur.
 *
 * Le navigateur garde donc son propre état d'édition, et le parcours n'apprend
 * la date qu'une fois complète. Même raison que pour les éditeurs Monaco des
 * leçons Reps, et même règle : le modèle du navigateur fait foi pendant la
 * frappe.
 *
 * L'écran est remonté à chaque changement d'étape (`key={etape}`), donc
 * `defaultValue` reprend bien la valeur du parcours quand on revient dessus.
 *
 * Posé seul sous une question, sans libellé ni `aria-label`, il prend le titre
 * de l'écran pour nom (ContexteEcran) : un lecteur d'écran annonçait sinon
 * « date » sans dire laquelle. Dans un Champ, c'est le libellé qui le nomme.
 */
export function ChampDate({ valeur, poser, className = "", nommeParLibelle = false, ...reste }) {
    const idTitre = useContext(ContexteEcran);
    const nomme = nommeParLibelle || reste["aria-label"] || reste["aria-labelledby"];
    return (
        <input
            type="date"
            defaultValue={valeur}
            onChange={(e) => poser(e.target.value)}
            className={className || `${STYLE_CHAMP} border-gray-200/70 dark:border-white/10`}
            aria-labelledby={nomme || !idTitre ? undefined : idTitre}
            {...reste}
        />
    );
}

/**
 * Un champ étiqueté : son libellé, sa saisie, son aide, et le rouge quand la
 * valeur est refusée.
 *
 * Les formulaires en avaient chacun une copie ; le défaut du champ de date les
 * a toutes trouvées en même temps.
 */
export function Champ({ libelle, aide, valeur, poser, manquant, erreur, type = "text", ...reste }) {
    /* Une valeur refusée ne se signale qu'une fois le champ quitté. En direct,
       le message clignoterait à chaque touche : taper « 1962 » dans un champ de
       date fait passer la valeur par 0001, 0019 puis 0196, trois années que la
       règle refuse à bon droit et dont aucune ne mérite d'être reprochée à
       quelqu'un qui est en train d'écrire. */
    const [quitte, setQuitte] = useState(false);
    const montrerErreur = Boolean(erreur) && quitte;
    const idMessage = useId();
    const message = montrerErreur || aide;

    const bordure = manquant
        ? "border-red-500/60"
        : montrerErreur
          ? "border-amber-500/60"
          : "border-gray-200/70 dark:border-white/10";

    const communs = {
        onBlur: () => setQuitte(true),
        className: `${STYLE_CHAMP} ${bordure}`,
        "aria-invalid": manquant || montrerErreur ? true : undefined,
        "aria-describedby": message ? idMessage : undefined,
    };

    return (
        <label className="block">
            <span className="block text-xs text-gray-500 dark:text-lightBlue/70 mb-1.5">
                {libelle}
            </span>
            {type === "date" ? (
                <ChampDate valeur={valeur} poser={poser} nommeParLibelle {...communs} {...reste} />
            ) : type === "textarea" ? (
                /* Trois lignes par défaut : de quoi voir qu'on peut écrire une
                   phrase entière, sans occuper l'écran d'un formulaire qui en
                   compte plusieurs. */
                <textarea
                    rows={3}
                    value={valeur}
                    onChange={(e) => poser(e.target.value)}
                    {...communs}
                    {...reste}
                />
            ) : (
                <input
                    type={type}
                    autoComplete="off"
                    value={valeur}
                    onChange={(e) => poser(e.target.value)}
                    {...communs}
                    {...reste}
                />
            )}
            {montrerErreur ? (
                <span id={idMessage} className="block text-xs text-amber-700 dark:text-amber-400 mt-1.5">
                    {erreur}
                </span>
            ) : (
                aide && (
                    <span id={idMessage} className="block text-xs text-gray-500 dark:text-lightBlue/70 mt-1.5">
                        {aide}
                    </span>
                )
            )}
        </label>
    );
}

/** Bouton d'avancement. */
export function Avancer({ onClick, children, pleineLargeur = false, disabled = false, type = "button" }) {
    const avecEntree = useContext(ContexteEntree);
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            data-avancer=""
            className={`group ${pleineLargeur ? "w-full" : "w-full sm:w-auto"} mt-6 inline-flex min-h-[48px] items-center justify-center gap-2 px-7 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-colors duration-200 hover:bg-[#B83C12] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#D04515]`}
        >
            {children}
            {/* La flèche dit « on avance », comme sur le bouton de /outils. */}
            <SolarIcon name="arrow-right-linear" className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5 group-disabled:translate-x-0" />
            {/* Entrée fait avancer sur cet écran : on le dit, discrètement. */}
            {avecEntree && (
                <kbd aria-hidden="true" className={`${TOUCHE} ml-1 h-5 bg-white/20 px-1.5 text-[11px] text-white/90`}>
                    ↵
                </kbd>
            )}
        </button>
    );
}

/** Lien de reprise, en pied de résultat. */
/**
 * Une énumération, en puces plutôt qu'en phrase à rallonge.
 *
 * Trois motifs séparés par des virgules se lisent mal au milieu d'un
 * paragraphe : le lecteur cherche le sien et doit relire. Le style reprend
 * celui des listes de la page d'un outil, pour qu'une même énumération se
 * présente pareil qu'on la lise dans le parcours ou dans la pédagogie.
 */
export function Liste({ items }) {
    return (
        <ul className="mt-2.5 space-y-1.5">
            {items.map((item) => (
                <li
                    key={item}
                    className="flex gap-2.5 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                >
                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
}

export function Recommencer({ onClick, libelle }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex w-full min-h-[44px] items-center justify-center gap-1 mt-3 -mb-3 text-sm text-gray-500 dark:text-lightBlue/70 hover:text-primary transition-colors duration-200"
        >
            <SolarIcon name="restart-linear" className="h-4 w-4" /> {libelle}
        </button>
    );
}

/**
 * Une rangée de boutons-choix compacts, pour les réglages d'un résultat.
 *
 * `libelle` affiche le libellé au-dessus et en nomme le groupe ; `labelledBy`
 * reste pour l'appelant qui pose son libellé ailleurs.
 */
export function Segments({ options, valeur, poser, labelledBy, libelle: libelleGroupe, grand = false }) {
    // Un groupe de boutons à bascule : chacun annonce s'il est choisi, et le
    // groupe porte le libellé affiché au-dessus.
    const idLibelle = useId();
    const groupe = (
        <div
            role="group"
            aria-labelledby={libelleGroupe ? idLibelle : labelledBy}
            className="flex flex-wrap gap-2"
        >
            {options.map(({ cle, libelle }) => (
                <button
                    key={cle}
                    type="button"
                    aria-pressed={valeur === cle}
                    onClick={() => poser(cle)}
                    // Les puces de la fiche de /outils : un contour qui s'allume,
                    // plutôt qu'un aplat orange qui criait plus fort que le
                    // bouton « Continuer ».
                    className={`${grand ? "min-h-[44px] px-4 py-2.5" : "min-h-[40px] px-3.5 py-2"} rounded-full border text-sm font-semibold transition-colors duration-200 ${
                        valeur === cle
                            ? "border-[#D04515] bg-[#fff4ee] text-[#B83C12] dark:bg-[#D04515]/15 dark:text-[#FF8A5B]"
                            : "border-gray-300 bg-white text-gray-700 hover:border-[#D04515] hover:bg-[#fff8f4] dark:border-white/20 dark:bg-transparent dark:text-lightBlue dark:hover:bg-white/5"
                    }`}
                >
                    {libelle}
                </button>
            ))}
        </div>
    );
    if (!libelleGroupe) return groupe;
    return (
        <div>
            <span id={idLibelle} className="block text-xs text-gray-500 dark:text-lightBlue/70 mb-1.5">
                {libelleGroupe}
            </span>
            {groupe}
        </div>
    );
}

/**
 * La valeur d'un curseur, qu'on peut aussi taper.
 *
 * Le curseur s'arrête à un maximum raisonnable pour rester précis sous le
 * doigt. Celui qui gagne plus n'a pas à se contenter de ce maximum : il tape
 * son montant ici, sans plafond, et le curseur reste calé au bout de sa
 * course. Pendant la frappe, le champ montre les chiffres bruts ; en dehors,
 * le montant formaté.
 */
function ValeurSaisie({ libelle, valeur, poser, minimum, formater }) {
    const [brouillon, setBrouillon] = useState(null);
    return (
        <label className="relative shrink-0">
            <span className="sr-only">{libelle}</span>
            <input
                type="text"
                inputMode="numeric"
                value={brouillon ?? formater(valeur)}
                onFocus={(e) => {
                    const champ = e.currentTarget;
                    setBrouillon(String(valeur));
                    requestAnimationFrame(() => champ.select());
                }}
                onChange={(e) => {
                    const chiffres = e.target.value.replace(/\D/g, "").slice(0, 9);
                    setBrouillon(chiffres);
                    if (chiffres) poser(Math.max(minimum, Number(chiffres)));
                }}
                onBlur={() => setBrouillon(null)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur();
                }}
                className="w-36 rounded-md bg-gray-100 dark:bg-white/[0.06] py-1 pl-2 pr-7 text-right text-sm font-bold tabular-nums dark:text-white focus:outline-hidden focus:ring-2 focus:ring-primary/40"
            />
            <SolarIcon
                name="pen-2-linear"
                className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-lightBlue/60"
            />
        </label>
    );
}

/**
 * Un curseur avec son libellé, sa valeur et son aide.
 *
 * `saisie` rend la valeur modifiable au clavier, au-delà du maximum : pour les
 * montants sans limite naturelle, un bénéfice ou un revenu. Un montant borné
 * par la loi ou par un autre champ, un plafond de la micro ou des dépenses qui
 * ne peuvent dépasser le chiffre d'affaires, garde son curseur seul.
 */
export function Curseur({ libelle, aide, valeur, poser, minimum = 0, maximum, pas = 500, formater, saisie = false }) {
    const idLibelle = useId();
    const idAide = useId();
    const borne = Math.min(Math.max(valeur, minimum), maximum);
    return (
        <div>
            <div className={`flex justify-between mb-1.5 gap-3 ${saisie ? "items-center" : "items-baseline"}`}>
                <span id={idLibelle} className="text-sm text-gray-600 dark:text-lightBlue">{libelle}</span>
                {saisie ? (
                    <ValeurSaisie
                        libelle={libelle}
                        valeur={valeur}
                        poser={poser}
                        minimum={minimum}
                        formater={formater}
                    />
                ) : (
                    <span className="text-sm font-bold tabular-nums dark:text-white shrink-0">
                        {formater(valeur)}
                    </span>
                )}
            </div>
            <input
                type="range"
                min={minimum}
                max={maximum}
                step={pas}
                value={borne}
                onChange={(e) => poser(Number(e.target.value))}
                aria-labelledby={idLibelle}
                aria-valuetext={formater(borne)}
                aria-describedby={aide ? idAide : undefined}
                className="w-full accent-primary cursor-pointer"
            />
            {aide && (
                <p id={idAide} className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1 mb-0">{aide}</p>
            )}
        </div>
    );
}

/**
 * Un écran de résultat, animé comme les autres.
 *
 * Il porte les traits pleins quand l'appelant lui passe ses phases : sans eux
 * la barre disparaissait à l'arrivée, et le seul écran qui méritait de la
 * montrer entière était celui qui ne la montrait pas.
 *
 * Son titre est un titre de niveau 2 qui prend le focus à l'arrivée, comme
 * celui d'une question. `onRetour` ramène à la dernière question, pour
 * corriger une réponse sans tout recommencer.
 */
export function Resultat({ titre, phases, sens, reduit, onRetour, retour, children }) {
    const ref = useFocusTitre(sens);
    // La page sait qu'un résultat est là : c'est le moment de « Et maintenant ? ».
    useEffect(() => {
        RESULTAT.poser(true);
        return () => RESULTAT.poser(false);
    }, []);
    return (
        <motion.div className={CADRE} {...entree(sens, reduit)}>
            {onRetour && (
                <div className="mb-3">
                    <BoutonRetour onClick={onRetour} libelle={retour} />
                </div>
            )}
            {phases && (
                <div className="mb-5">
                    <Traits
                        phases={phases}
                        phase={phases.length - 1}
                        complet
                        reduit={reduit}
                    />
                </div>
            )}
            <h2
                ref={ref}
                tabIndex={-1}
                className="text-[11px] font-semibold text-[#D04515] dark:text-[#FF8A5B] uppercase tracking-wider outline-hidden mb-0"
            >
                {titre}
            </h2>
            {/* LE RÉSULTAT SE DÉCOUVRE DANS L'ORDRE DE SA LECTURE : le verdict,
                puis les montants, puis le détail, quarante millisecondes
                d'écart. Un bloc déjà là ne rejoue rien quand on règle un
                paramètre du résultat : la cascade ne vaut qu'à l'arrivée. */}
            {Children.map(children, (enfant, i) =>
                enfant === null || enfant === undefined || enfant === false ? enfant : (
                    <motion.div {...cascade(i, sens, reduit)}>{enfant}</motion.div>
                ),
            )}
        </motion.div>
    );
}

/**
 * Progression d'un compteur de résultat.
 *
 * À appeler dans un composant MONTÉ AVEC L'ÉCRAN DE RÉSULTAT, jamais en tête du
 * parcours : l'effet ne tourne qu'au premier montage, et appelé en haut du
 * simulateur il finit sa course quatre dixièmes de seconde après le chargement
 * de la page, bien avant que le lecteur n'atteigne le résultat. Trois agents
 * ont trouvé le défaut sur la référence et l'ont contourné chacun de leur côté.
 * `Montant` ci-dessous est la façon simple de ne pas se tromper.
 */
export function useMontants(reduit) {
    return useProgression(reduit ? 0 : DUREE_COMPTEUR);
}

/** Un montant qui monte à l'apparition de l'écran, formaté en euros par défaut. */
export function Montant({ valeur, lang, reduit, formater = euros, className = "" }) {
    const p = useMontants(reduit);
    return <span className={`tabular-nums ${className}`}>{formater(valeur * p, lang)}</span>;
}

/** Un taux : « 12,3 % » en français, "12.3%" en anglais, sans décimale inutile. */
export function pourcent(taux, lang, decimales = 1) {
    const v = (taux * 100).toFixed(decimales).replace(/\.0+$/, "");
    return lang === "fr" ? `${v.replace(".", ",")}\u00A0%` : `${v}%`;
}

/** Format monétaire au centime, pour une indemnité par jour. */
export function eurosPrecis(v, lang) {
    return `${v.toLocaleString(lang === "fr" ? "fr-FR" : "en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}\u00A0€`;
}

/** Remplit tous les {trous} d'un libellé en une passe. */
export function remplir(texte, valeurs) {
    return Object.entries(valeurs).reduce(
        (t, [cle, v]) => t.replaceAll(`{${cle}}`, String(v)),
        texte,
    );
}

/**
 * Format monétaire commun aux outils.
 *
 * Espace insécable avant le symbole : un montant ne se coupe jamais entre le
 * nombre et son euro en fin de ligne. C'est la seule convention typographique
 * que les outils tiennent, parce qu'elle vit ici et nulle part ailleurs.
 */
export function euros(v, lang) {
    return `${Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}\u00A0€`;
}

/** Format de date commun aux outils, en UTC pour ne pas glisser d'un jour. */
export function dateLisible(d, lang) {
    return d.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });
}

export { motion };
